import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, TrendingUp, AlertTriangle, Sparkles } from "lucide-react";
import { BrowserFrame, DashboardPreview, ScaledFrame } from "@/components/landing/previews";
import { FadeUp, WordReveal, Tilt, EASE } from "@/components/landing/cinematic";

/* Floating insight chips that orbit the hero product frame. */
function FloatChip({ className = "", delay = 0, children, testId }) {
    return (
        <motion.div
            data-testid={testId}
            initial={{ opacity: 0, y: 18, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay, ease: EASE }}
            className={`absolute z-20 hidden lg:block ${className}`}
        >
            <div className="animate-chip-float flex items-center gap-2 rounded-xl border border-[#1c1917]/10 bg-white/90 px-3 py-2 text-[11.5px] font-medium text-[#33302b] shadow-[0_18px_50px_-18px_rgba(28,25,23,0.28)] backdrop-blur-md">
                {children}
            </div>
        </motion.div>
    );
}

const MODULES = [
    "Dashboard", "Product Brain", "Inventory Intelligence", "Warehouse Hub",
    "Customer Brain", "Supplier Brain", "Sales Hub", "Billing", "Finance Hub",
    "Business Intelligence", "Procurement", "YugOS Intelligence · AI", "User Management",
];

const STATS = [
    { v: "30 sec", l: "Business health" },
    { v: "30 min", l: "Onboarding" },
    { v: "13", l: "Connected modules" },
    { v: "1", l: "AI copilot" },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const frameY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const chipY = useTransform(scrollYProgress, [0, 1], [0, 170]);
    const glowOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <>
            <section ref={ref} className="relative overflow-visible pt-28 sm:pt-36 lg:pt-44" data-testid="hero-section">
                {/* Atmosphere — warm radial + feathered grid */}
                <motion.div style={{ opacity: glowOpacity }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[560px] w-[900px] rounded-full bg-[radial-gradient(closest-side,rgba(67,83,228,0.09),rgba(194,65,12,0.05),transparent)] blur-2xl" />
                    <div
                        className="absolute inset-0 opacity-[0.5]"
                        style={{
                            backgroundImage:
                                "linear-gradient(rgba(28,25,23,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(28,25,23,0.06) 1px, transparent 1px)",
                            backgroundSize: "52px 52px",
                            maskImage: "radial-gradient(ellipse at 50% 0%, black 25%, transparent 72%)",
                            WebkitMaskImage: "radial-gradient(ellipse at 50% 0%, black 25%, transparent 72%)",
                        }}
                    />
                </motion.div>

                <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                    <div className="max-w-5xl">
                        <FadeUp>
                            <div className="inline-flex items-center gap-2 rounded-full border border-[#1c1917]/12 bg-white/70 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.16em] text-[#59524a] backdrop-blur">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                </span>
                                Now live at Yug Traders LLP
                            </div>
                        </FadeUp>

                        <h1 className="mt-7 sm:mt-8 font-display font-bold tracking-[-0.035em] text-[#1c1917] leading-[1.03] text-[38px] sm:text-6xl lg:text-[76px]">
                            <WordReveal text="The AI Operating System" delay={0.05} as="span" className="block" />
                            <span className="block">
                                <WordReveal text="for wholesale" delay={0.32} as="span" />
                                <motion.span
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.6, duration: 0.8 }}
                                    className="font-editorial font-medium text-[#c2410c]"
                                >
                                    {" "}&amp;{" "}
                                </motion.span>
                                <WordReveal text="retail." delay={0.5} as="span" />
                            </span>
                        </h1>

                        <FadeUp delay={0.55}>
                            <p className="mt-7 max-w-xl text-[17px] sm:text-lg leading-[1.65] text-[#59524a]">
                                Built inside a real wholesale business.{" "}
                                <span className="font-editorial text-[#1c1917]">Designed for every one of them.</span>
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.7}>
                            <div className="mt-9 flex flex-wrap items-center gap-3">
                                <Link to="/login" data-testid="hero-launch-btn">
                                    <span className="group inline-flex h-12 items-center gap-2 rounded-full bg-[#1c1917] pl-6 pr-5 text-[14.5px] font-medium text-[#f7f3ec] shadow-[0_16px_40px_-16px_rgba(28,25,23,0.5)] transition-[background-color,transform] duration-200 hover:bg-[#33302b] hover:-translate-y-0.5">
                                        Launch YugOS
                                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                                    </span>
                                </Link>
                                <a href="#product" data-testid="hero-demo-btn">
                                    <span className="inline-flex h-12 items-center rounded-full border border-[#1c1917]/20 px-6 text-[14.5px] font-medium text-[#1c1917] transition-colors duration-200 hover:bg-[#1c1917] hover:text-[#f7f3ec]">
                                        Explore the product
                                    </span>
                                </a>
                            </div>
                        </FadeUp>
                    </div>

                    {/* Product frame — oversized, tilting, breaking into the next band */}
                    <div className="relative mt-12 sm:mt-16 lg:mt-20 -mb-14 sm:-mb-28 lg:-mb-40 z-10">
                        <motion.div style={{ y: frameY }} className="hidden lg:block">
                            <FadeUp delay={0.35} y={44}>
                                <Tilt max={3.2}>
                                    <div className="relative lg:-mx-10 xl:-mx-16">
                                        <div className="absolute -inset-6 rounded-[32px] bg-gradient-to-b from-[#4353e4]/[0.08] via-transparent to-transparent blur-2xl pointer-events-none" />
                                        <BrowserFrame
                                            url="yugos.app/app/dashboard"
                                            className="relative shadow-[0_60px_130px_-45px_rgba(28,25,23,0.45)]"
                                        >
                                            <DashboardPreview />
                                        </BrowserFrame>
                                    </div>
                                </Tilt>
                            </FadeUp>
                        </motion.div>

                        {/* Mobile: scaled desktop preview, no tilt, no parallax */}
                        <div className="lg:hidden">
                            <FadeUp delay={0.25} y={32}>
                                <div className="relative overflow-hidden rounded-2xl border border-[#1c1917]/10 bg-white shadow-[0_28px_70px_-30px_rgba(28,25,23,0.35)]">
                                    <ScaledFrame designWidth={860} designHeight={520}>
                                        <BrowserFrame url="yugos.app/app/dashboard">
                                            <DashboardPreview />
                                        </BrowserFrame>
                                    </ScaledFrame>
                                </div>
                            </FadeUp>
                        </div>

                        <motion.div style={{ y: chipY }} className="absolute inset-0 pointer-events-none">
                            <FloatChip className="-top-7 right-2 xl:-right-8" delay={1.0} testId="hero-chip-brief">
                                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                                Morning brief ready · Healthy 82
                            </FloatChip>
                            <FloatChip className="top-1/3 -left-12 xl:-left-24" delay={1.2} testId="hero-chip-overdue">
                                <AlertTriangle className="h-3.5 w-3.5 text-[#c2410c]" strokeWidth={2} />
                                ₹2.84L overdue flagged
                            </FloatChip>
                            <FloatChip className="bottom-14 -right-3 xl:-right-10" delay={1.4} testId="hero-chip-brain">
                                <Sparkles className="h-3.5 w-3.5 text-[#4353e4]" strokeWidth={2} />
                                Intelligence: 6 SKUs to reorder
                            </FloatChip>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Signal strip — stats + module marquee on deeper paper */}
            <section className="relative bg-[#efe8dc] pt-24 sm:pt-40 lg:pt-56 pb-0" data-testid="signal-strip">
                <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                    <FadeUp>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 sm:gap-y-10 border-t border-[#1c1917]/10 pt-8 sm:pt-10 pb-12 sm:pb-14">
                            {STATS.map((s, i) => (
                                <div
                                    key={s.l}
                                    className={`${i > 0 ? "md:border-l md:border-[#1c1917]/10 md:pl-10" : ""} ${
                                        i % 2 === 1 ? "border-l border-[#1c1917]/10 pl-5 md:pl-10" : ""
                                    }`}
                                >
                                    <div className="font-display text-[32px] sm:text-5xl font-bold tracking-tight text-[#1c1917]">
                                        {s.v}
                                    </div>
                                    <div className="mt-1.5 sm:mt-2 text-[10.5px] font-mono uppercase tracking-[0.2em] text-[#59524a]">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>

                <div className="relative border-t border-[#1c1917]/[0.08] py-5 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-[#efe8dc] to-transparent z-10 pointer-events-none" />
                    <div className="absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-[#efe8dc] to-transparent z-10 pointer-events-none" />
                    <div className="flex animate-marquee whitespace-nowrap">
                        {[...MODULES, ...MODULES].map((m, i) => (
                            <span
                                key={`${m}-${i}`}
                                className="mx-5 sm:mx-7 inline-flex items-center gap-2 sm:gap-2.5 font-mono text-[11px] sm:text-[12px] uppercase tracking-[0.14em] text-[#59524a]"
                            >
                                <span className={`h-1 w-1 rounded-full ${m.includes("AI") ? "bg-[#4353e4]" : "bg-[#c2410c]/50"}`} />
                                {m}
                            </span>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
